import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./DecoyPaywall.module.scss"

/**
 * DecoyPaywall — an original, self-contained FEATURE-SPECIFIC paywall.
 *
 * Concept ("Hide your vault in plain sight"): triggered when the user taps the
 * locked DECOY VAULT / app-disguise feature. Most feature paywalls *describe*
 * the feature; the existing "Caught" one *performs* a security camera. This one
 * performs a different trick entirely — MISDIRECTION. The screen opens disguised
 * as a deliberately boring, ordinary phone gallery (a parking lot, a houseplant,
 * some groceries) — the exact decoy you'd hand to anyone who forces you to unlock
 * your phone. A line tells you: "This is all they'll ever find."
 *
 * Then you perform the secret gesture — press and hold the hidden handle — and
 * the disguise PEELS AWAY like a false floor, light spilling from the seam, to
 * reveal your REAL encrypted vault glowing underneath: your actual memories,
 * locked, that only you know exist. The whole moment is a two-faced UI that lies
 * to you on purpose, then flips — deception as the medium, a reveal you operate.
 *
 * The layout funnels to the YEARLY plan: arming Decoy Vault for a year is the
 * only large, gold-foil-bordered, tappable card (selected by default), the annual
 * price is the single biggest number on screen (with a tiny per-month equivalent
 * so the commitment feels small and it stays App Store-compliant — the annual
 * price is the most prominent price for the annual product), a 7-day free trial
 * removes friction, savings vs monthly is shouted, and monthly is demoted to one
 * quiet line. It never implies the user loses access to anything — hiding is not
 * losing.
 *
 * Fully self-contained: no shared paywall components are reused. Reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data. Reuses the
 * brand fonts (RedHatDisplay + KronaOne for the engraved wordmark / annual price),
 * the brand primary blue (the real vault glow) and secondary gold (the foil, the
 * locks, the CTA), and existing repo feature icons (decoy, disguise, face-down
 * lock, pattern lock). Memory + decoy photos are bundled under /img/decoy so they
 * render offline and on the live site, each with a gradient onError fallback.
 */

const HOLD_MS = 1050
const AUTO_REVEAL_MS = 7000
const DUST_COUNT = 16

// The boring "decoy" gallery the user would hand over. Deliberately dull.
const DECOY_TILES = [
  "decoy1.jpg",
  "decoy2.jpg",
  "decoy3.jpg",
  "decoy4.jpg",
  "decoy5.jpg",
  "decoy6.jpg",
  "decoy7.jpg",
  "decoy8.jpg",
  "decoy9.jpg",
]

// The real memories hidden underneath — emotional, personal, encrypted.
const MEMORY_TILES = [
  { img: "mem1.jpg", caption: "Maui, 2023" },
  { img: "mem3.jpg", caption: "Us, in Rome" },
  { img: "mem2.jpg", caption: "Mom's 60th" },
  { img: "mem4.jpg", caption: "Cooper's first day" },
  { img: "mem5.jpg", caption: "The summit, finally" },
  { img: "mem6.jpg", caption: "The day she arrived" },
]

const PROPS: { Icon: React.FC; title: string; body: string }[] = [
  {
    Icon: svg.featureIcons.Decoy,
    title: "A vault that lies for you",
    body: "Hand over a believable fake gallery on demand. They never know there's more.",
  },
  {
    Icon: svg.featureIcons.Disguise,
    title: "Disguise the whole app",
    body: "Make it look like a calculator, notes or weather on your home screen.",
  },
  {
    Icon: svg.featureIcons.PatternLock,
    title: "A second secret PIN",
    body: "One code opens the decoy. A different one opens what's really there.",
  },
  {
    Icon: svg.featureIcons.FacedownLock,
    title: "Vanishes instantly",
    body: "Flip your phone face-down and the real vault seals itself in a blink.",
  },
]

const DecoyPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData
  const reduceMotion = useReducedMotion()

  const [revealed, setRevealed] = useState(false)
  const [holding, setHolding] = useState(false)
  const [showClose, setShowClose] = useState(false)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  const revealedRef = useRef(false)
  revealedRef.current = revealed

  // --- pricing -------------------------------------------------------------
  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const memoryCount =
    productData?.numPhotos && productData.numPhotos > 0
      ? productData.numPhotos
      : 2174

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  // --- native --------------------------------------------------------------
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#070912")
    actions.setFooterInsetColor("#070912")
    const t = setTimeout(() => setShowClose(true), 2600)
    return () => clearTimeout(t)
  }, [actions])

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined =
      selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // --- the hold-to-reveal mechanism ---------------------------------------
  const handleRef = useRef<HTMLButtonElement>(null)
  const rafRef = useRef<number | undefined>(undefined)
  const holdStartRef = useRef<number | null>(null)

  const doReveal = useCallback(() => {
    if (revealedRef.current) return
    setHolding(false)
    setRevealed(true)
    // briefly announce to the native layer so analytics / haptics can fire
    native.logMixpanelEvent("decoy_revealed")
  }, [])

  const setHoldVar = (v: number) => {
    handleRef.current?.style.setProperty("--hold", v.toFixed(3))
  }

  const tick = useCallback(
    (now: number) => {
      if (holdStartRef.current === null) return
      const elapsed = now - holdStartRef.current
      const k = Math.min(1, elapsed / HOLD_MS)
      setHoldVar(k)
      if (k >= 1) {
        doReveal()
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    },
    [doReveal],
  )

  const startHold = useCallback(() => {
    if (revealedRef.current) return
    setHolding(true)
    holdStartRef.current = performance.now()
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(tick)
  }, [tick])

  const cancelHold = useCallback(() => {
    if (revealedRef.current) return
    holdStartRef.current = null
    setHolding(false)
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    // gently rewind the ring
    let v = parseFloat(
      handleRef.current?.style.getPropertyValue("--hold") || "0",
    )
    const rewind = () => {
      v = Math.max(0, v - 0.08)
      setHoldVar(v)
      if (v > 0 && holdStartRef.current === null) {
        rafRef.current = requestAnimationFrame(rewind)
      }
    }
    rafRef.current = requestAnimationFrame(rewind)
  }, [])

  // Reduced motion: present the vault already revealed so the offer is never
  // gated behind a gesture. Otherwise a gentle safety auto-reveal guarantees the
  // offer is always reachable even with no interaction.
  useEffect(() => {
    if (reduceMotion) {
      setRevealed(true)
      return
    }
    const t = setTimeout(() => {
      if (!revealedRef.current) doReveal()
    }, AUTO_REVEAL_MS)
    return () => {
      clearTimeout(t)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [reduceMotion, doReveal])

  return (
    <div
      className={styles.screen}
      data-revealed={revealed ? "1" : "0"}
      data-reduce={reduceMotion ? "1" : "0"}
    >
      <Backdrop reduceMotion={!!reduceMotion} />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.55, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 16, paddingBottom: bottomInset + 18 }}
      >
        {/* ================= THE STAGE (disguise → real vault) ============= */}
        <div className={styles.stage}>
          {/* --- underneath: the REAL vault --- */}
          <div className={styles.vault} aria-hidden={!revealed}>
            <div className={styles.vaultHead}>
              <span className={styles.vaultBadge}>
                <LockGlyph />
                REAL VAULT · ENCRYPTED
              </span>
              <h2 className={styles.vaultTitle}>
                {memoryCount.toLocaleString()} memories,
                <br />
                hidden in plain sight
              </h2>
            </div>

            <div className={styles.memGrid}>
              {MEMORY_TILES.map((m, i) => (
                <div
                  key={m.img}
                  className={styles.memTile}
                  style={{ "--i": i } as React.CSSProperties}
                >
                  <Img name={m.img} className={styles.memImg} />
                  <span className={styles.memShade} />
                  <span className={styles.memLock}>
                    <LockGlyph />
                  </span>
                  <span className={styles.memCaption}>{m.caption}</span>
                  <span className={styles.memSheen} />
                </div>
              ))}
            </div>
          </div>

          {/* --- on top: the boring DECOY gallery that peels away --- */}
          <div className={styles.disguise}>
            <div className={styles.disguiseInner}>
              <div className={styles.fakeBar}>
                <span className={styles.fakeTitle}>Photos</span>
                <span className={styles.fakeCount}>1,204 items</span>
              </div>
              <div className={styles.fakeSeg}>
                {["Years", "Months", "Days", "All"].map((s, i) => (
                  <span
                    key={s}
                    className={`${styles.fakeSegItem} ${
                      i === 3 ? styles.fakeSegOn : ""
                    }`}
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div className={styles.decoyGrid}>
                {DECOY_TILES.map((d, i) => (
                  <div
                    key={d}
                    className={styles.decoyTile}
                    style={{ "--i": i } as React.CSSProperties}
                  >
                    <Img name={d} className={styles.decoyImg} />
                  </div>
                ))}
              </div>

              <div className={styles.decoyFooter}>
                {/* the "this is the decoy" tag */}
                <div className={styles.decoyTag}>
                  <EyeGlyph />
                  <span>
                    This is the <b>decoy</b>. It's all they'll ever find.
                  </span>
                </div>

                {/* the secret hold-to-reveal handle */}
                <div className={styles.handleZone}>
                  <button
                    ref={handleRef}
                    className={`${styles.handle} ${
                      holding ? styles.handleOn : ""
                    }`}
                    style={{ "--hold": 0 } as React.CSSProperties}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      startHold()
                    }}
                    onPointerUp={cancelHold}
                    onPointerLeave={cancelHold}
                    onPointerCancel={cancelHold}
                    aria-label="Press and hold to reveal your real vault"
                  >
                    <svg
                      className={styles.handleRing}
                      viewBox="0 0 80 80"
                      aria-hidden
                    >
                      <circle
                        className={styles.handleTrack}
                        cx="40"
                        cy="40"
                        r="35"
                      />
                      <circle
                        className={styles.handleProg}
                        cx="40"
                        cy="40"
                        r="35"
                      />
                    </svg>
                    <FingerprintGlyph />
                  </button>
                  <span className={styles.handleHint}>
                    {holding
                      ? "Hold…"
                      : "Press & hold to reveal the real vault"}
                  </span>
                </div>
              </div>
            </div>

            {/* the light seam that flashes as the disguise lifts */}
            <span className={styles.seam} aria-hidden />
          </div>
        </div>

        {/* ================= THE OFFER (revealed) ========================= */}
        <AnimatePresence>
          {revealed && (
            <motion.div
              className={styles.offer}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.35,
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <div className={styles.intro}>
                <span className={styles.eyebrow}>
                  <span className={styles.eyebrowDot} />
                  FEATURE · DECOY VAULT
                </span>
                <h1 className={styles.title}>
                  Hide your vault{" "}
                  <span className={styles.titleAccent}>in plain sight.</span>
                </h1>
                <p className={styles.subtitle}>
                  If you're ever forced to unlock your phone, hand over the
                  decoy. Your real memories stay invisible — only you know they
                  exist.
                </p>
              </div>

              <div className={styles.props}>
                {PROPS.map((p, i) => (
                  <motion.div
                    key={p.title}
                    className={styles.prop}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                  >
                    <span className={styles.propIcon}>
                      <p.Icon />
                    </span>
                    <span className={styles.propText}>
                      <span className={styles.propTitle}>{p.title}</span>
                      <span className={styles.propBody}>{p.body}</span>
                    </span>
                  </motion.div>
                ))}
              </div>

              <div className={styles.plans}>
                <button
                  className={`${styles.yearCard} ${
                    selected === "yearly" ? styles.yearCardActive : ""
                  }`}
                  onClick={() => setSelected("yearly")}
                  aria-pressed={selected === "yearly"}
                >
                  <span className={styles.foilEdge} aria-hidden />
                  <span className={styles.bestRibbon}>SAVE {savings}%</span>

                  <div className={styles.yearHead}>
                    <span
                      className={`${styles.radio} ${
                        selected === "yearly" ? styles.radioOn : ""
                      }`}
                      aria-hidden
                    >
                      {selected === "yearly" && <Check />}
                    </span>
                    <div className={styles.yearHeadText}>
                      <span className={styles.yearName}>Arm for a year</span>
                      <span className={styles.yearSub}>
                        Best value · stays hidden
                      </span>
                    </div>
                  </div>

                  <div className={styles.priceBlock}>
                    <div className={styles.priceRow}>
                      <span className={styles.priceMain}>{yearlyPrice}</span>
                      <span className={styles.pricePer}>/year</span>
                    </div>
                    <div className={styles.priceSub}>
                      7 days free, then just {perMonthEquivalent}/mo · billed
                      annually
                    </div>
                  </div>
                </button>

                <button
                  className={`${styles.monthlyLine} ${
                    selected === "monthly" ? styles.monthlyLineOn : ""
                  }`}
                  onClick={() => setSelected("monthly")}
                  aria-pressed={selected === "monthly"}
                >
                  Prefer monthly? {monthlyPrice}/mo — standard rate
                </button>
              </div>

              <div className={styles.ctaWrap}>
                <button className={styles.cta} onClick={handlePurchase}>
                  <span className={styles.ctaShimmer} aria-hidden />
                  <span className={styles.ctaLabel}>
                    {selected === "yearly"
                      ? "Arm Decoy Vault — 7 days free"
                      : "Arm Decoy Vault monthly"}
                  </span>
                  <span className={styles.ctaArrow}>→</span>
                </button>
                <p className={styles.ctaNote}>
                  No charge today · Cancel anytime in one tap
                </p>
              </div>

              <TrustRow />

              <div className={styles.legal}>
                <button onClick={() => native.postNativeEvent("terms")}>
                  Terms
                </button>
                <span>·</span>
                <button onClick={() => native.postNativeEvent("privacy")}>
                  Privacy
                </button>
                <span>·</span>
                <button
                  onClick={() => native.postNativeEvent("already_purchased")}
                >
                  Restore
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Backdrop: a dark vault ambiance — a deep blue glow that warms as the */
/* disguise lifts, drifting dust, a faint grid, a focusing vignette.    */
/* ------------------------------------------------------------------ */
const Backdrop = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const dust = useMemo(
    () =>
      Array.from({ length: DUST_COUNT }, (_, i) => {
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        return {
          left: `${r1 * 100}%`,
          size: 1.5 + r3 * 3,
          delay: `${r2 * 10}s`,
          dur: `${11 + r1 * 10}s`,
          drift: `${(r3 - 0.5) * 44}px`,
        }
      }),
    [],
  )

  return (
    <div className={styles.scene} aria-hidden>
      <div className={styles.glow} />
      <div className={styles.grid} />
      {!reduceMotion && (
        <div className={styles.dust}>
          {dust.map((d, i) => (
            <span
              key={i}
              className={styles.mote}
              style={
                {
                  left: d.left,
                  width: d.size,
                  height: d.size,
                  animationDelay: d.delay,
                  animationDuration: d.dur,
                  "--drift": d.drift,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div className={styles.vignette} />
    </div>
  )
}

/* A bundled image with a gradient onError fallback so every tile still reads. */
const Img = ({ name, className }: { name: string; className: string }) => (
  <img
    className={className}
    src={`/img/decoy/${name}`}
    alt=""
    loading="eager"
    onError={(e) => {
      const el = e.currentTarget
      el.style.visibility = "hidden"
    }}
  />
)

const TrustRow = () => (
  <div className={styles.trust}>
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>4.8★</span>
      <span className={styles.trustLabel}>App Store</span>
    </div>
    <span className={styles.trustSep} />
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>10M+</span>
      <span className={styles.trustLabel}>vaults</span>
    </div>
    <span className={styles.trustSep} />
    <div className={styles.trustItem}>
      <span className={styles.trustStrong}>Zero-knowledge</span>
      <span className={styles.trustLabel}>only you hold the key</span>
    </div>
  </div>
)

/* ---- inline glyphs ---- */
const LockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <rect
      x="5"
      y="10.5"
      width="14"
      height="10"
      rx="2.4"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.2" r="1.5" fill="currentColor" />
  </svg>
)

const EyeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <path
      d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
  </svg>
)

const FingerprintGlyph = () => (
  <svg
    className={styles.handleIcon}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    width="100%"
    height="100%"
  >
    <path
      d="M12 2.6a9.4 9.4 0 0 0-9 6.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 6.2a5.8 5.8 0 0 0-5.7 6.9c.2 1.2.2 2.5-.1 3.7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 9.8a2.2 2.2 0 0 0-2.2 2.5c.2 2 .1 4-.5 5.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 9.8a2.2 2.2 0 0 1 2.2 2.5c-.1 1.3-.1 2.6.1 3.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M15.8 9.4a5.8 5.8 0 0 1 1.9 4.6c0 1 .1 2 .4 3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 13.4c.2 2.6.1 5.2-.6 7.7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default DecoyPaywall
